<template>
  <a-collapse
    v-model:activeKey="siteConfigSectionKeys"
    :bordered="false"
    class="site-config-sections"
  >
    <a-collapse-panel
      key="solar-delivery"
      header="Solar delivery method"
      :force-render="true"
      data-tn="site-config-section-solar-delivery"
    >
      <a-alert
        v-if="!isCommissioned"
        type="warning"
        message="It seems this Solshare has not been commissioned yet."
        banner
        data-tn="site-config-not-commissioned"
      />
      <template v-else>
        <a-select
          style="width: 100%"
          v-model:value="form.algorithmType"
          :loading="loading"
          @change="methodChanged"
          :disabled="isReadonly"
          data-tn="solshare-info-solar-delivery-method"
        >
          <a-select-option
            v-if="!is2P"
            :value="ALGORITHM_TYPE.DEMAND_BASED_DELIVERY"
            data-tn="solshare-info-solar-delivery-method-demand-based-delivery"
          >
            Demand-based delivery
          </a-select-option>

          <a-select-option
            :value="ALGORITHM_TYPE.CUSTOM_ALLOCATION"
            data-tn="solshare-info-solar-delivery-method-custom-allocation"
          >
            Custom allocation
          </a-select-option>
        </a-select>

        <div class="site-config-solar-distribution">
          <div class="site-config-solar-distribution__head">
            <span class="site-config-solar-distribution__title">Solar Distribution</span>
            <a-radio-group
              v-if="shouldDisplayWeightInput"
              v-model:value="suffix"
              :disabled="isReadonly"
            >
              <a-radio-button
                :value="UNEQUAL_ALLOCATION_TYPE.PERCENT"
                data-tn="solshare-info-unequal-percentage"
              >
                %
              </a-radio-button>
              <a-radio-button :value="UNEQUAL_ALLOCATION_TYPE.KW" data-tn="solshare-info-unequal-kw">
                kW
              </a-radio-button>
            </a-radio-group>
          </div>
          <ConnectionGroup
            v-for="phaseIndex in is2P ? [0, 1] : [0, 1, 2]"
            :key="phaseIndex"
            :phase-index="phaseIndex"
            :loading="loading"
            v-model:algorithm-weight="form.algorithmWeight[phaseIndex]"
            v-model:enabled-units="form.enabledUnits[phaseIndex]"
            :connected-units="connectedUnits.filter((u) => u.phase === phaseIndex)"
            :should-display-weight-input="shouldDisplayWeightInput"
            :available="available"
            :suffix="suffix"
            :ref="`groups`"
            :readonly="isReadonly"
            default-even
          />
          <div class="site-config-solar-distribution__footer">
            <a-button
              type="primary"
              @click="saveClicked"
              :loading="saveLoading"
              :disabled="isReadonly"
              data-tn="save-site-config"
            >
              {{ isReadonly ? "We currently don't support changing this in this region" : 'Save' }}
            </a-button>
          </div>
        </div>
      </template>
    </a-collapse-panel>

    <a-collapse-panel
      key="software-version"
      header="Software version"
      data-tn="site-config-section-software-version"
    >
      <a-spin v-if="softwareLoading" :spinning="true" />
      <div
        v-else-if="softwareLoadError"
        class="connectivity"
        data-tn="site-config-software-versions-error"
      >
        <span class="connectivity__value">{{ softwareLoadError }}</span>
      </div>
      <div v-else class="connectivity" data-tn="site-config-software-versions">
        <div v-if="hasSolshareVersionReported" class="site-config-version-grid">
          <div class="connectivity__block" data-tn="site-config-solshare-version">
            <div class="connectivity__row">
              <span class="connectivity__label">SolShare version</span>
            </div>
            <div class="connectivity__row connectivity__row--value">
              <span class="connectivity__value" data-tn="site-config-solshare-version-value">{{
                solshareVersionDisplay
              }}</span>
            </div>
          </div>
        </div>
        <div v-else-if="hasAnyComponentReported" class="site-config-version-grid">
          <div class="connectivity__block" data-tn="site-config-firmware-version">
            <div class="connectivity__row">
              <span class="connectivity__label">Firmware version</span>
            </div>
            <div class="connectivity__row connectivity__row--value">
              <span class="connectivity__value" data-tn="site-config-firmware-version-value">{{
                firmwareVersionDisplay
              }}</span>
            </div>
          </div>
          <div class="connectivity__block" data-tn="site-config-application-version">
            <div class="connectivity__row">
              <span class="connectivity__label">Application version</span>
            </div>
            <div class="connectivity__row connectivity__row--value">
              <span class="connectivity__value" data-tn="site-config-application-version-value">{{
                applicationVersionDisplay
              }}</span>
            </div>
          </div>
          <div
            v-if="isMk2Device"
            class="connectivity__block site-config-version-grid__opsafe"
            data-tn="site-config-opsafe-version"
          >
            <div class="connectivity__row">
              <span class="connectivity__label">OpSafe version</span>
            </div>
            <div class="connectivity__row connectivity__row--value">
              <span class="connectivity__value" data-tn="site-config-opsafe-version-value">{{
                opsafeVersionDisplay
              }}</span>
            </div>
          </div>
        </div>
        <div v-else data-tn="site-config-software-no-data">
          <span class="connectivity__value">No data available</span>
        </div>
      </div>
    </a-collapse-panel>

    <a-collapse-panel
      key="additional-information"
      header="Additional information"
      :force-render="true"
      data-tn="site-config-section-additional-information"
    >
      <a-spin v-if="loading" :spinning="true" />
      <div v-else class="connectivity" data-tn="site-config-connectivity">
        <div class="site-config-two-col">
          <div class="connectivity__block" data-tn="site-config-wifi-status">
            <div class="connectivity__row">
              <span class="connectivity__label">{{ connectivityLabelText }}</span>
            </div>
            <div class="connectivity__row connectivity__row--value">
              <span class="connectivity__value" data-tn="site-config-wifi-ssid">{{
                connectivityValueDisplay
              }}</span>
            </div>
          </div>
          <div class="connectivity__block" data-tn="site-config-last-online">
            <div class="connectivity__row">
              <span class="connectivity__label">Connectivity Status</span>
            </div>
            <div class="connectivity__row connectivity__row--value">
              <span class="connectivity__value" data-tn="site-config-last-online-value">{{
                connectivityStatusDisplay
              }}</span>
            </div>
          </div>
        </div>
        <p class="connectivity__hint">{{ connectivityHint }}</p>
      </div>
    </a-collapse-panel>
  </a-collapse>
</template>

<script>
import ConnectionGroup from './ConnectionGroup';
import {
  DEFAULT_ALGO_WEIGHT,
  SOLAR_DELIVERY_METHOD,
  UNEQUAL_ALLOCATION_TYPE,
  ALGORITHM_TYPE,
  COUNTRY_CODE,
} from '@/utils/constants';
import semver from 'semver';
import { backendToFrontend } from '../../../../utils/array';
import {
  getAlgorithmTypeFromSolarDeliveryMethod,
  getSolarDeliveryMethodFromAlgorithmType,
} from '@/shared/utils/solar-delivery-method';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { getSolshareVersion } from '@/shared/utils/solshare-serial';
import { SOLSHARE_VERSION } from '@/modules/commissioning/utils/constants';
import { AppContext } from '@/models/AppContext';
import { useAuthStore } from '@/shared/stores/authStore';

dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

/** Match main-server commissioned-device online threshold (commission.service). */
const DEVICE_ONLINE_THRESHOLD_SECONDS = 300;

export default {
  name: 'SolshareInfo',
  emits: ['config-saved'],
  components: {
    ConnectionGroup,
  },
  data() {
    return {
      SOLAR_DELIVERY_METHOD,
      UNEQUAL_ALLOCATION_TYPE,
      ALGORITHM_TYPE,
      // Local snapshot used for diffing on subsequent saves.
      // Never assign to the `oldConfig` prop directly.
      oldConfigSnapshot: null,
      form: {
        solarDeliveryMethod: '',
        algorithmType: '',
        connectedUnits: [[], [], []],
        enabledUnits: [[], [], []],
        algorithmWeight: [],
      },
      validate: false,
      saveLoading: false,
      suffix: UNEQUAL_ALLOCATION_TYPE.PERCENT,
      // Empty = all panels start collapsed; user expands sections as needed
      siteConfigSectionKeys: [],
      softwareLoading: false,
      softwareLoadError: null,
      softwareState: null,
      softwareFetchedForSerial: null,
    };
  },
  props: {
    data: Object,
    oldConfig: Object,
    serialNum: String,
    loading: Boolean,
    isCommissioned: {
      type: Boolean,
      default: true,
    },
  },
  watch: {
    data(v) {
      this.suffix = UNEQUAL_ALLOCATION_TYPE.PERCENT;
      this.updateForm(v);
      this.syncSuffixForProjectCountry();
    },
    serialNum() {
      this.resetSoftwareState();
      if (this.siteConfigSectionKeys.includes('software-version')) {
        this.fetchSoftwareState();
      }
    },
    siteConfigSectionKeys(keys) {
      if (keys.includes('software-version')) {
        this.fetchSoftwareState();
      }
    },
    oldConfig: {
      handler(v) {
        // Keep a safe, mutable snapshot without mutating the prop.
        this.oldConfigSnapshot = v ? JSON.parse(JSON.stringify(v)) : null;
      },
      immediate: true,
      deep: false,
    },
  },
  created() {
    this.updateForm(this.data);
    this.syncSuffixForProjectCountry();
  },
  methods: {
    syncSuffixForProjectCountry() {
      if (this.data?.meter?.project?.country === COUNTRY_CODE['United Kingdom']) {
        this.$nextTick(() => {
          this.suffix = UNEQUAL_ALLOCATION_TYPE.KW;
        });
      }
    },
    versionDisplay(value) {
      return value === undefined || value === null || value === ''
        ? 'No data available'
        : String(value);
    },
    componentReported(slug) {
      const components = this.softwareState?.components;
      if (!Array.isArray(components)) {
        return null;
      }
      const match = components.find((c) => c?.slug === slug);
      return match?.reported ?? null;
    },
    resetSoftwareState() {
      this.softwareState = null;
      this.softwareLoadError = null;
      this.softwareFetchedForSerial = null;
      this.softwareLoading = false;
    },
    async fetchSoftwareState() {
      if (!this.serialNum || this.softwareLoading) {
        return;
      }
      // Skip only after a successful fetch for this serial; failed loads may retry on reopen.
      if (this.softwareFetchedForSerial === this.serialNum && this.softwareState != null) {
        return;
      }
      this.softwareLoading = true;
      this.softwareLoadError = null;
      try {
        const { data } = await this.$http.get(this.$api.getSolshareSoftware(this.serialNum));
        this.softwareState = data;
        this.softwareFetchedForSerial = this.serialNum;
      } catch (e) {
        console.error(e);
        this.softwareState = null;
        this.softwareLoadError =
          (e && e.message) || 'Failed to load software version';
      } finally {
        this.softwareLoading = false;
      }
    },
    updateForm(v) {
      this.form.solarDeliveryMethod = v?.config?.algorithm_type || null;
      this.form.connectedUnits = v?.config?.connected_units || [[], [], []];
      this.form.enabledUnits = v?.config?.enabled_units || [[], [], []];
      this.form.algorithmWeight = backendToFrontend(v?.config?.algo_weight || [], this.is2P);
      this.form.algorithmType = getAlgorithmTypeFromSolarDeliveryMethod(
        this.form.solarDeliveryMethod,
      );
    },

    methodChanged(val) {
      this.form.solarDeliveryMethod = getSolarDeliveryMethodFromAlgorithmType(val, {
        is2P: this.$store.getters.is2P,
        isUnequalAlgorithm: this.isUnequalAlgorithm,
      });

      if (this.shouldDisplayWeightInput) {
        this.form.algorithmWeight = this.is2P
          ? DEFAULT_ALGO_WEIGHT.slice(0, 2)
          : DEFAULT_ALGO_WEIGHT.slice();
      } else {
        this.form.algorithmWeight = [];
      }
    },
    async saveClicked() {
      this.saveLoading = true;
      try {
        const groupsRef = this.$refs.groups;
        const groups = Array.isArray(groupsRef) ? groupsRef : groupsRef ? [groupsRef] : [];
        await Promise.all(groups.map((g) => g.validate()));

        await this.$http.post(this.$api.uploadConfig(this.serialNum), {
          solarDeliveryMethod: this.form.solarDeliveryMethod,
          enabledUnits: this.form.enabledUnits,
          ...(this.shouldDisplayWeightInput && {
            algorithmWeight: this.form.algorithmWeight,
          }),
          oldConfig: this.oldConfigSnapshot ?? this.oldConfig,
        });
        this.$message.success('Configuration saved successfully');
        this.$emit('config-saved');
      } catch (e) {
        console.error(e);
        if (e && e.errorFields && e.errorFields.length > 0) {
          this.$message.warn(e.errorFields.map((field) => field.errors.join(', ')).join('\n'));
        } else if (e && e.message) {
          this.$message.warn(e.message);
        } else if (typeof e === 'string') {
          this.$message.warn(e);
        } else {
          this.$message.warn('Please check your input');
        }
      }
      this.saveLoading = false;
    },
  },
  computed: {
    is2P() {
      return this.serialNum.startsWith('2P');
    },
    isMk2Device() {
      return getSolshareVersion(this.serialNum) === SOLSHARE_VERSION.Mk2;
    },
    isUnequalAlgorithm() {
      const isMk1UnequalSerial =
        /3P_35A_\d{4}(_test)?/.test(this.serialNum) || /2P_100_\d{4}(_test)?/.test(this.serialNum);

      const isMk2UnequalSerial = getSolshareVersion(this.serialNum) === SOLSHARE_VERSION.Mk2; // All Mk2 can have unequal allocation

      if (!isMk1UnequalSerial && !isMk2UnequalSerial) {
        return false;
      }
      const swVer = semver.valid(this.data?.meter?.softwareVersion) || '0.0.0';
      return semver.gte(swVer, '0.4.5');
    },
    isReadonly() {
      const userEmail = useAuthStore().profileEmail(AppContext.SolCentre);
      const isAllumeEmail =
        userEmail &&
        (/@allumeenergy\.com\.au$/.test(userEmail) || /@allumeenergy\.com$/.test(userEmail));

      if (isAllumeEmail) {
        console.log(`User email: ${userEmail} is allume ${isAllumeEmail}`);
        return false;
      }

      return (
        this.data?.meter?.project?.country === COUNTRY_CODE['United Kingdom'] ||
        this.data?.meter?.project?.country === COUNTRY_CODE['Australia']
      );
    },
    shouldDisplayWeightInput() {
      return (
        this.form.algorithmType === ALGORITHM_TYPE.CUSTOM_ALLOCATION && this.isUnequalAlgorithm
      );
    },

    available() {
      return Number(this.data?.meter?.systemSize) / (this.is2P ? 2 : 3);
    },

    wifiConnected() {
      return Boolean(this.data?.wifi?.connected);
    },
    connectionType() {
      return this.data?.wifi?.connectionType ?? 'unknown';
    },
    usesTransportConnectivity() {
      return (
        this.isMk2Device &&
        (this.connectionType === 'ethernet' || this.connectionType === 'wifi')
      );
    },
    connectivityLabelText() {
      if (this.usesTransportConnectivity) {
        return 'Connected via';
      }
      return this.wifiConnected ? 'Connected to' : 'Last connected to';
    },
    /** Presentation-only: build human-readable transport label from connectionType + ssid. */
    connectivityValueDisplay() {
      if (!this.usesTransportConnectivity) {
        return this.wifiSsidDisplay;
      }
      if (this.connectionType === 'ethernet') {
        return 'Ethernet';
      }
      if (this.connectionType === 'wifi') {
        return this.wifiTransportDisplay;
      }
      return this.wifiSsidDisplay;
    },
    wifiTransportDisplay() {
      const ssid = typeof this.data?.wifi?.ssid === 'string' ? this.data.wifi.ssid.trim() : '';
      if (!ssid || ssid === 'N/A') {
        return 'Wi-Fi';
      }
      return `Wi-Fi (${ssid})`;
    },
    connectivityHint() {
      if (this.isMk2Device && this.usesTransportConnectivity) {
        return 'Network details as last reported by the device.';
      }
      return 'Wi‑Fi details as last reported by the device.';
    },
    wifiSsidDisplay() {
      const ssid = this.data?.wifi?.ssid;
      return ssid === undefined || ssid === null || ssid === '' ? 'N/A' : ssid;
    },
    deviceRecentlySeen() {
      const ts = this.data?.wifi?.lastOnlineAt;
      if (ts == null) {
        return false;
      }
      return Math.abs(Math.round(Date.now() / 1000) - ts) <= DEVICE_ONLINE_THRESHOLD_SECONDS;
    },
    connectivityStatusDisplay() {
      const ts = this.data?.wifi?.lastOnlineAt;
      if (ts == null) {
        return 'No data yet';
      }
      if (this.deviceRecentlySeen) {
        return 'Online now';
      }
      const d = dayjs.unix(ts);
      // LL / LTS need dayjs localizedFormat plugin (otherwise literals "LL" / "LTS" appear).
      return `Last online ${d.format('LL')}, ${d.format('LTS')} (${d.fromNow()})`;
    },
    firmwareVersionDisplay() {
      return this.versionDisplay(this.componentReported('fw'));
    },
    applicationVersionDisplay() {
      return this.versionDisplay(this.componentReported('app'));
    },
    opsafeVersionDisplay() {
      return this.versionDisplay(this.componentReported('opsafe'));
    },
    hasSolshareVersionReported() {
      const reported = this.softwareState?.solshareVersion?.reported;
      return reported !== undefined && reported !== null && reported !== '';
    },
    solshareVersionDisplay() {
      return this.versionDisplay(this.softwareState?.solshareVersion?.reported);
    },
    hasAnyComponentReported() {
      const slugs = this.isMk2Device ? ['fw', 'app', 'opsafe'] : ['fw', 'app'];
      return slugs.some((slug) => {
        const reported = this.componentReported(slug);
        return reported !== undefined && reported !== null && reported !== '';
      });
    },
    connectedUnits() {
      if (!this.form.connectedUnits) {
        return [];
      }
      return this.form.connectedUnits
        .map((phase, i) => {
          return phase.map((conn) => {
            const phase = `L${i + 1}-${conn + 1}`;
            const unit = this.data.unit[conn][`L${i + 1}`];

            return {
              unit: unit || phase,
              enabled: Array.isArray(this.form.enabledUnits)
                ? this.form.enabledUnits[i].includes(conn)
                : true,
              key: `${i}${conn}`,
              phase: i,
              conn,
            };
          });
        })
        .flat();
    },
  },
};
</script>

<style lang="less" scoped>
.site-config-sections {
  margin-bottom: 16px;
  border-radius: 2px;

  :deep(.ant-collapse-item) {
    border: 1px solid #f0f0f0;
    margin-bottom: 8px;
    border-radius: 2px;
    overflow: hidden;
    background: #fafafa;
  }

  :deep(.ant-collapse-header) {
    font-weight: 600;
    background: #fafafa !important;
    color: rgba(0, 0, 0, 0.85);
  }

  :deep(.ant-collapse-content) {
    background: #ffffff !important;
    border-top: 1px solid #f0f0f0;
  }

  :deep(.ant-collapse-content-box) {
    padding: 16px 16px 20px;
    background: #ffffff;
  }
}

.site-config-solar-distribution {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
}

.site-config-solar-distribution__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
}

.site-config-solar-distribution__title {
  font-weight: 600;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.85);
}

.site-config-solar-distribution__footer {
  margin-top: 16px;
}

.site-config-two-col {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px 24px;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
}

.site-config-version-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px 24px;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
}

.site-config-version-grid__opsafe {
  @media (min-width: 768px) {
    grid-column: 1 / -1;
  }
}

.connectivity {
  font-size: 14px;
  line-height: 1.5;
}

.connectivity__block {
  min-width: 0;
}

.connectivity__row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  margin-bottom: 4px;
}

.connectivity__row--value {
  margin-bottom: 0;
}

.connectivity__label {
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
  min-width: 0;
}

.connectivity__value {
  color: rgba(0, 0, 0, 0.85);
  word-break: break-word;
}

.connectivity__hint {
  margin: 12px 0 0;
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
}
</style>
